import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * How long (ms) the pointer may sit genuinely outside the open menu region before we collapse
 * it. A small grace makes diagonal travel toward a submenu link forgiving and prevents a
 * flicker-close when the cursor skims the very edge of the panel.
 */
const CLOSE_DELAY_MS = 150;

/**
 * A guard window (ms) after the last scroll/wheel gesture during which we refuse to *schedule*
 * a close. Tertiary safety net only: the primary defences are (a) never closing on the
 * per-<li> pointerleave and (b) ignoring pointer events the hand did not cause. Kept short so a
 * deliberate move-away right after a scroll still closes promptly.
 */
const SCROLL_GUARD_MS = 150;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    document.addEventListener('scroll', this.#scrollListener, { passive: true, capture: true });
    window.addEventListener('wheel', this.#scrollListener, { passive: true });
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);

    // Region-based close model. An open mega menu is dismissed by GENUINE pointer movement out
    // of the header+panel region, a click outside, Escape, or keyboard blur — never by the
    // per-<li> pointerleave, which the browser ALSO fires when the sticky header slides up under
    // a stationary cursor while the page scrolls (that was the "menu schiet weg op scroll" bug).
    document.addEventListener('pointermove', this.#pointerMoveListener, { passive: true });
    document.addEventListener('pointerdown', this.#pointerDownListener, { passive: true });
    document.addEventListener('keydown', this.#keydownListener);
    // Pointer flicks off the top/side of the window entirely.
    document.documentElement.addEventListener('pointerleave', this.#windowPointerLeaveListener, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.removeEventListener('scroll', this.#scrollListener, { capture: true });
    window.removeEventListener('wheel', this.#scrollListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    document.removeEventListener('pointermove', this.#pointerMoveListener);
    document.removeEventListener('pointerdown', this.#pointerDownListener);
    document.removeEventListener('keydown', this.#keydownListener);
    document.documentElement.removeEventListener('pointerleave', this.#windowPointerLeaveListener);
    this.#clearCloseTimer();
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  /**
   * Timestamp (performance.now) of the most recent scroll/wheel gesture.
   * @type {number}
   */
  #lastScrollAt = 0;

  #scrollListener = () => {
    this.#lastScrollAt = performance.now();
  };

  /**
   * Last processed pointer position, used to drop pointer events the hand did not actually
   * cause (unchanged client coords).
   * @type {number | null}
   */
  #lastPointerX = null;
  #lastPointerY = null;

  /**
   * Pending "close because the pointer left the menu" timer.
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #closeTimer = null;

  #clearCloseTimer() {
    if (this.#closeTimer != null) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
  }

  /**
   * The combined screen rectangle of the open menu: the header row plus the open submenu panel
   * plus the mega-menu underlay, merged into one seamless box from the top of the header to the
   * bottom of the panel. Geometry-based (not elementFromPoint), so it is independent of z-order
   * and correctly includes the panel's padding/backdrop — parts of the visible menu that are NOT
   * DOM descendants of the active <li>.
   * @returns {DOMRect | null}
   */
  #openMenuRect() {
    const header = this.headerComponent;
    if (!header) return null;

    /** @type {DOMRect[]} */
    const rects = [header.getBoundingClientRect()];

    const panel = findSubmenu(this.#state.activeItem);
    if (panel) rects.push(panel.getBoundingClientRect());

    // The visible mega-menu backdrop; its height tracks --full-open-header-height, so it spans
    // the full painted area of the open menu.
    const underlay = header.querySelector('.header__underlay-open');
    if (underlay) rects.push(underlay.getBoundingClientRect());

    let top = Infinity;
    let bottom = -Infinity;
    let left = Infinity;
    let right = -Infinity;

    for (const r of rects) {
      if (r.width === 0 && r.height === 0) continue;
      top = Math.min(top, r.top);
      bottom = Math.max(bottom, r.bottom);
      left = Math.min(left, r.left);
      right = Math.max(right, r.right);
    }

    if (top === Infinity) return null;
    return new DOMRect(left, top, right - left, bottom - top);
  }

  /**
   * Is the point within the open menu region (with a small tolerance for edge skims / the
   * sticky top:-1px trick)?
   * @param {number} x
   * @param {number} y
   */
  #isInsideOpenMenu(x, y) {
    const rect = this.#openMenuRect();
    if (!rect) return false;
    const pad = 6;
    return x >= rect.left - pad && x <= rect.right + pad && y >= rect.top - pad && y <= rect.bottom + pad;
  }

  /**
   * Central pointer-driven close. Only GENUINE hand movement can close the menu here.
   * @param {PointerEvent} event
   */
  #pointerMoveListener = (event) => {
    const x = event.clientX;
    const y = event.clientY;

    // Two independent ways to detect "the hand did not actually move": no device delta, and
    // unchanged client coordinates. When the page scrolls under a still cursor the browser
    // re-dispatches pointermove (to keep :hover correct) but the pointer never moved — both
    // checks catch it. This is what makes the menu immune to scroll, even minutes later.
    const deviceStill = event.movementX === 0 && event.movementY === 0;
    const positionStill = x === this.#lastPointerX && y === this.#lastPointerY;

    this.#lastPointerX = x;
    this.#lastPointerY = y;

    if (!this.#state.activeItem || deviceStill || positionStill) return;

    if (this.#isInsideOpenMenu(x, y)) {
      // Back inside (e.g. the user moved up toward the menu after a scroll shifted it) — keep open.
      this.#clearCloseTimer();
      return;
    }

    // Genuinely outside the menu. Don't schedule a close as a side effect of an in-progress
    // scroll gesture; a real move-away a moment later still closes it.
    if (performance.now() - this.#lastScrollAt < SCROLL_GUARD_MS) return;

    if (this.#closeTimer == null) {
      this.#closeTimer = setTimeout(() => {
        this.#closeTimer = null;
        this.#deactivate();
      }, CLOSE_DELAY_MS);
    }
  };

  /**
   * Click/tap anywhere outside the header closes the open menu (standard dropdown dismissal).
   * @param {PointerEvent} event
   */
  #pointerDownListener = (event) => {
    if (!this.#state.activeItem) return;
    if (!(event.target instanceof Node)) return;
    if (this.headerComponent?.contains(event.target)) return;
    this.#deactivate();
  };

  /**
   * Escape closes the open menu and returns focus to its trigger (keyboard accessibility).
   * @param {KeyboardEvent} event
   */
  #keydownListener = (event) => {
    if (event.key !== 'Escape' || !this.#state.activeItem) return;
    const trigger = this.#state.activeItem;
    this.#deactivate();
    if (trigger instanceof HTMLElement) trigger.focus();
  };

  /**
   * The pointer left the browser window entirely — close.
   */
  #windowPointerLeaveListener = () => {
    if (this.#state.activeItem) this.#deactivate();
  };

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    // Switching to another top-level item — cancel any pending close from the previous one.
    this.#clearCloseTimer();

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Bound to each top-level item's on:pointerleave and on:blur.
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    // POINTER: ignore the per-<li> pointerleave entirely. The browser fires it not only on a real
    // mouse-out but also when the sticky header slides up under a stationary cursor during scroll,
    // which is indistinguishable here and was collapsing the menu on scroll. Pointer-driven
    // closing is owned centrally by #pointerMoveListener (genuine movement out of the header+panel
    // region) and #pointerDownListener (click outside). A layout shift never produces a genuine
    // pointermove, so the menu correctly stays open until the user actually moves away — even long
    // after the scroll has settled.
    if (typeof PointerEvent !== 'undefined' && event instanceof PointerEvent) return;

    // KEYBOARD (blur / focusout): close when focus genuinely leaves the menu.
    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    this.#clearCloseTimer();

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
